import {
  CATEGORIES_OPTIONS_IDS,
  SurveyBuilderConfig,
  SurveyBuilderRootDocument,
  SurveyBuilderVersionDocument,
} from "@max/common/dist/setfan";
import { validateField } from "./builderConfigSchema";

export const buildSurveyBuilderConfig = (
  survey?: SurveyBuilderRootDocument,
  version?: SurveyBuilderVersionDocument,
): SurveyBuilderConfig => ({
  artistGroupId: survey?.artistGroupId ?? "",
  artistPolicy: version?.artistPolicy ?? "",
  disclaimers: version?.disclaimers ?? [],
  fields: {
    description: {
      content: version?.fields?.description?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.description.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    header: {
      content: version?.fields?.header?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.header.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    body: {
      content: version?.fields?.body?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.body.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    // NOTE: our original survey creator took one of (backgroundColor, backgroundImage).
    // not sure our current goals but keep this in mind since they both validate
    // individually
    backgroundImage: {
      content: version?.fields?.backgroundImage?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.backgroundImage.content.en !== undefined
          ? false
          : "Select a background image",
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    image: {
      content: version?.fields?.image?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.image.content.en !== undefined
          ? false
          : "Select an image",
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    backgroundColor: {
      content: version?.fields?.backgroundColor?.content ?? "#fff",
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.backgroundColor.content !== undefined
          ? false
          : "Select a background color",
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    buttonColor: {
      content: version?.fields?.buttonColor?.content ?? "#0d0a0a",
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.buttonColor.content !== undefined
          ? false
          : "Select a button color",
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    layout: {
      content: version?.fields?.layout?.content ?? "default",
      getValidation: () => false,
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    texture: {
      content: version?.fields?.texture?.content ?? "none",
      getValidation: () => false,
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    darkMode: {
      content: version?.fields?.darkMode?.content ?? false,
      getValidation: () => false,
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    isContest: {
      content: version?.fields?.isContest?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.isContest.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    contestPrize: {
      content: version?.fields?.contestPrize?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.contestPrize.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    contestEndDate: {
      content: version?.fields?.contestEndDate?.content ?? null,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.contestEndDate.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    contestPrizeValue: {
      content: version?.fields?.contestPrizeValue?.content ?? null,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.contestPrizeValue.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    contestCustomPrizeDescription: {
      content: version?.fields?.contestCustomPrizeDescription?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField(
          "fields.contestCustomPrizeDescription.content.en",
          config,
        ),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    hasContestCustomRules: {
      content:
        version?.fields?.hasContestCustomRules?.content === undefined
          ? false
          : version?.fields?.hasContestCustomRules?.content,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.hasContestCustomRules.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    contestCustomRulesUrl: {
      content: version?.fields?.contestCustomRulesUrl?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.contestCustomRulesUrl.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    numberOfCategories: {
      content: version?.fields?.numberOfCategories?.content ?? "3",
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.numberOfCategories.content !== undefined
          ? false
          : "Enter a value",
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    displayedCategories: {
      content: version?.fields?.displayedCategories?.content ?? [
        ...CATEGORIES_OPTIONS_IDS,
      ],
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.displayedCategories.content.length >= 3
          ? false
          : "Select at least 3 categories",
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    demographicsIntroText: {
      content: version?.fields?.demographicsIntroText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.demographicsIntroText.content.en !== undefined
          ? false
          : "Enter a value",
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    fooddrinkIntroText: {
      content: version?.fields?.fooddrinkIntroText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.fooddrinkIntroText.content.en !== undefined
          ? false
          : "Enter a value",
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    alcoholIntroText: {
      content: version?.fields?.alcoholIntroText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.alcoholIntroText.content.en !== undefined
          ? false
          : "Enter a value",
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    clothingIntroText: {
      content: version?.fields?.clothingIntroText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.clothingIntroText.content.en !== undefined
          ? false
          : "Enter a value",
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    vehicleIntroText: {
      content: version?.fields?.vehicleIntroText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.vehicleIntroText.content.en !== undefined
          ? false
          : "Enter a value",
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    entertainmentIntroText: {
      content: version?.fields?.entertainmentIntroText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        config.fields.entertainmentIntroText.content.en !== undefined
          ? false
          : "Enter a value",
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    phoneNumberValidation: {
      content: version?.fields?.phoneNumberValidation?.content ?? "required",
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.phoneNumberValidation.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    thankYouHeader: {
      content: version?.fields?.thankYouHeader?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.thankYouHeader.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    thankYouBody: {
      content: version?.fields?.thankYouBody?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.thankYouBody.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    hasFeaturedCta: {
      content: version?.fields?.hasFeaturedCta?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.hasFeaturedCta.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    featuredCtaHeaderText: {
      content: version?.fields?.featuredCtaHeaderText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.featuredCtaHeaderText.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    featuredCtaButtonText: {
      content: version?.fields?.featuredCtaButtonText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.featuredCtaButtonText.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    featuredCtaUrl: {
      content: version?.fields?.featuredCtaUrl?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.featuredCtaUrl.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    useCustomTermsAndPrivacyPolicy: {
      content:
        version?.fields?.useCustomTermsAndPrivacyPolicy?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.useCustomTermsAndPrivacyPolicy.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    customTermsAndPrivacyPolicy: {
      content: version?.fields?.customTermsAndPrivacyPolicy?.content ?? {
        en:
          version.optIns?.find((optIn) => optIn.id === "optInTermsPrivacy1")
            ?.htmlLabel || "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.customTermsAndPrivacyPolicy.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    termsAndPrivacyPolicySeparateLineItems: {
      content: version?.fields?.termsAndPrivacyPolicySeparateLineItems
        ?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField(
          "fields.termsAndPrivacyPolicySeparateLineItems.content.en",
          config,
        ),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    useCustomSmsOptIn: {
      content: version?.fields?.useCustomSmsOptIn?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.useCustomSmsOptIn.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    customSmsOptIn: {
      content: version?.fields?.customSmsOptIn?.content ?? {
        en:
          version.optIns?.find((optIn) => optIn.id === "optInText")
            ?.htmlLabel || "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.customSmsOptIn.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    hasIntroFooter: {
      content: version?.fields?.hasIntroFooter?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.hasIntroFooter.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    introFooter: {
      content: version?.fields?.introFooter?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.introFooter.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    hasThankYouFooter: {
      content: version?.fields?.hasIntroFooter?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.hasThankYouFooter.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    thankYouFooter: {
      content: version?.fields?.thankYouFooter?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.thankYouFooter.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },

    // Pre-Saves
    hasPreSave: {
      content: version?.fields?.hasPreSave?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.hasPreSave.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    preSaveRelease: {
      content: version?.fields?.preSaveRelease?.content ?? null,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.preSaveRelease.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    hasPreSaveExtraContestEntries: {
      content: version?.fields?.hasPreSaveExtraContestEntries?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.hasPreSaveExtraContestEntries.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    preSaveExtraContestEntries: {
      content: version?.fields?.preSaveExtraContestEntries?.content ?? 1,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.preSaveExtraContestEntries.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    preSaveMessage: {
      content: version?.fields?.preSaveMessage?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.preSaveMessage.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    preSaveThankYouMessage: {
      content: version?.fields?.preSaveThankYouMessage?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.preSaveThankYouMessage.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    preSaveContestMessage: {
      content: version?.fields?.preSaveContestMessage?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.preSaveContestMessage.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    preSaveContestThankYouMessage: {
      content: version?.fields?.preSaveContestThankYouMessage?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField(
          "fields.preSaveContestThankYouMessage.content.en",
          config,
        ),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    hasSpotifyPlaylist: {
      content: version?.fields?.hasSpotifyPlaylist?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.hasSpotifyPlaylist.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    spotifyPlaylistText: {
      content: version?.fields?.spotifyPlaylistText?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.spotifyPlaylistText.content.en", config),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    spotifyPlaylistGivesSweepstakesEntries: {
      content:
        version?.fields?.spotifyPlaylistGivesSweepstakesEntries?.content ??
        false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField(
          "fields.spotifyPlaylistGivesSweepstakesEntries.content",
          config,
        ),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    spotifyPlaylistSweepstakesEntriesCount: {
      content:
        version?.fields?.spotifyPlaylistSweepstakesEntriesCount?.content ?? 1,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField(
          "fields.spotifyPlaylistSweepstakesEntriesCount.content",
          config,
        ),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    spotifyPlaylistSuccessMessage: {
      content: version?.fields?.spotifyPlaylistSuccessMessage?.content ?? {
        en: "",
      },
      getValidation: (config: SurveyBuilderConfig) =>
        validateField(
          "fields.spotifyPlaylistSuccessMessage.content.en",
          config,
        ),
      isTranslateable: true,
      isDirty: false,
      isTouched: false,
    },
    isRestrictedToAdmins: {
      content: version?.fields?.isRestrictedToAdmins?.content ?? false,
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.isRestrictedToAdmins.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    metaPixel: {
      content: version?.fields?.metaPixel?.content ?? "",
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.metaPixel.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
    tiktokPixel: {
      content: version?.fields?.tiktokPixel?.content ?? "",
      getValidation: (config: SurveyBuilderConfig) =>
        validateField("fields.tiktokPixel.content", config),
      isTranslateable: false,
      isDirty: false,
      isTouched: false,
    },
  },
  flags: {
    showContestConfirmationMessage:
      version?.flags?.showContestConfirmationMessage ?? null,
  },
  optIns: version?.optIns ?? [],
  surveyQuestions: version?.surveyQuestions ?? [],
});
